import { useEffect, useState } from 'react'
import { ChevronLeft, UserX } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EssentialMetadata } from './EssentialMetadata'
import { ProFeatureGate } from './ProFeatureGate'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { VisitorContextDrawer } from './VisitorContextDrawer'
import { PLACEHOLDER_VISITOR_METADATA } from './placeholderData'
import { CustomScrollArea } from './CustomScrollArea'
import { useTranslation } from '@/hooks/useTranslation'

interface ChatConversation {
	id: string
	visitor_name?: string
	visitor_email?: string
	visitor_metadata?: any
	visitor_context_restricted?: boolean
	status: 'open' | 'closed'
	[key: string]: any
}

interface VisitorContextSidebarProps {
	conversation?: ChatConversation
	isOpen: boolean
	onToggle: () => void
	isPro: boolean
	className?: string
	visitorName?: string
	visitorEmail?: string
	isLoading?: boolean
}

export function VisitorContextSidebar({
	conversation,
	isOpen,
	onToggle,
	isPro,
	className,
	visitorName: fallbackVisitorName,
	visitorEmail: fallbackVisitorEmail,
	isLoading = false,
}: VisitorContextSidebarProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const isMobile = useMediaQuery('(max-width: 767px)')
	const [recentlyChanged, setRecentlyChanged] = useState<string[]>([])

	// Handle keyboard shortcuts - must be before any early returns
	useEffect(() => {
		// Don't add keyboard shortcuts on mobile
		if (isMobile) return

		const handleKeyDown = (e: KeyboardEvent) => {
			// Ctrl/Cmd + I to toggle
			if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
				e.preventDefault()
				onToggle()
			}

			// Escape to close if open
			if (e.key === 'Escape' && isOpen) {
				onToggle()
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [isOpen, onToggle, isMobile])

	// Use drawer on mobile
	if (isMobile) {
		return (
			<VisitorContextDrawer
				conversation={conversation}
				isOpen={isOpen}
				onToggle={onToggle}
				isPro={isPro}
			/>
		)
	}

	// Don't render if collapsed
	if (!isOpen) {
		return null
	}

	const metadata = conversation?.visitor_metadata
	const isRestricted = conversation?.visitor_context_restricted === true
	const hasMetadata = metadata && Object.keys(metadata).length > 0

	// Determine what to show:
	// 1. Restricted: Show blurred placeholders (free user with data)
	// 2. Has metadata: Show real data (pro user)
	// 3. Empty: Show empty state (no data captured)
	const showRestricted = isRestricted && !hasMetadata
	const showRealData = hasMetadata && !isRestricted
	const showEmpty = !hasMetadata && !isRestricted

	// Use placeholder data when restricted
	const displayMetadata = showRestricted
		? PLACEHOLDER_VISITOR_METADATA
		: metadata
	const displayVisitorName = showRestricted
		? PLACEHOLDER_VISITOR_METADATA.name
		: conversation?.visitor_name || fallbackVisitorName
	const displayVisitorEmail = showRestricted
		? PLACEHOLDER_VISITOR_METADATA.email
		: conversation?.visitor_email || fallbackVisitorEmail

	return (
		<aside
			role="complementary"
			aria-label={t('visitorContextSidebar.visitorInformation')}
			aria-expanded={isOpen}
			className={cn(
				'flex-shrink-0 w-80 border-l bg-background/95 backdrop-blur-sm',
				'flex flex-col h-full overflow-hidden',
				className
			)}>
			<div className="flex items-center justify-between px-4 py-3 border-b">
				<h2 className="text-sm font-semibold text-foreground">
					{t('visitorContextSidebar.visitorContext')}
				</h2>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 hover:bg-muted"
					onClick={onToggle}
					aria-label={t('visitorContextSidebar.closeVisitorInformationSidebar')}>
					<ChevronLeft className="h-4 w-4" />
				</Button>
			</div>

			{/* Content */}
			<CustomScrollArea className="flex-1">
				<div className="p-3 space-y-3">
					{isLoading && !conversation && !fallbackVisitorName ? (
						<LoadingSkeleton />
					) : showEmpty ? (
						<EmptyState />
					) : (
						<>
							{/* Show upgrade prompt at the top for restricted users */}
							{showRestricted && (
								<ProFeatureGate
									onUpgradeClick={() => navigate('/settings?tab=billing')}
								/>
							)}

							<EssentialMetadata
								metadata={displayMetadata}
								isPro={isPro}
								isRestricted={showRestricted}
								recentlyChanged={recentlyChanged}
								visitorName={displayVisitorName}
								visitorEmail={displayVisitorEmail}
							/>
						</>
					)}
				</div>
			</CustomScrollArea>
		</aside>
	)
}

// Loading skeleton
function LoadingSkeleton() {
	const { t } = useTranslation()
	return (
		<div
			className="space-y-4 animate-pulse"
			aria-label={t('visitorContextSidebar.loadingVisitorInformation')}>
			<div className="space-y-2">
				<div className="h-3 bg-muted rounded w-20" />
				<div className="h-8 bg-muted rounded" />
				<div className="h-8 bg-muted rounded" />
			</div>
			<div className="space-y-2">
				<div className="h-3 bg-muted rounded w-24" />
				<div className="h-8 bg-muted rounded" />
			</div>
			<div className="space-y-2">
				<div className="h-3 bg-muted rounded w-16" />
				<div className="h-8 bg-muted rounded" />
				<div className="h-8 bg-muted rounded" />
				<div className="h-8 bg-muted rounded" />
			</div>
		</div>
	)
}

// Empty state
function EmptyState() {
	const { t } = useTranslation()
	return (
		<div className="text-center py-12 px-4">
			<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/50 flex items-center justify-center">
				<UserX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
			</div>
			<p className="text-sm font-medium text-foreground mb-1">
				{t('visitorContextSidebar.noVisitorInformationAvailable')}
			</p>
			<p className="text-xs text-muted-foreground leading-relaxed">
				{t('visitorContextSidebar.contextWillBeCaptured')}
			</p>
		</div>
	)
}
